import {IProductPriceObject} from './IProduct';
import ProductModel from './ProductModel';
import VariantModel from './VariantModel';

/**
 * Represents Product AbstractPriceModel
 * By default, the price model is the product price model
 */
abstract class AbstractPriceModel {
	product: ProductModel | VariantModel;

	/**
	 * @construct
	 * @param {ProductModel|VariantModel} product - product model
	 */
	constructor(product: ProductModel | VariantModel) {
		this.product = product;
	}

	/**
	 * Returns is sales price book applied
	 * @returns {boolean} - is sales price book applied
	 */
	isSalesPriceBookApplied(): boolean {
		return !!this.product?.isSalePriceBookApplied();
	}

	/**
	 * Check that product has price range on product variation group level
	 * @returns  {boolean} - product has price range
	 */
	hasPriceRange(): boolean {
		return false;
	}

	/**
	 * Returns the sales product price
	 * @return {IProductPriceObject}
	 */
	getSales() {
		return this.getMinPrice();
	}

	/**
	 * Returns the list product price
	 * @return {IProductPriceObject}
	 */
	getList() {
		const minPrice = this.getMinPrice();
		const maxPrice = this.getMaxPrice();

		if (minPrice && maxPrice && minPrice.value !== maxPrice.value) {
			return maxPrice;
		} else {
			return null;
		}
	}

	/**
	 * Returns the maximum price
	 * @returns {IProductPriceObject} - the maximum price
	 */
	getMaxPrice(): IProductPriceObject {
		const priceRanges = this.product.getPriceRanges();

		// assuming that the maxPrice should be available on top product level
		if (priceRanges?.length) {
			const maxPrice: IProductPriceObject = priceRanges.reduce(
				(maxPriceObj: IProductPriceObject, priceRange) => {
					if (priceRange.maxPrice > maxPriceObj.value) {
						return {
							currency: this.product.getPriceCurrency(),
							value: priceRange.maxPrice,
							pricebook: priceRange.pricebook,
						};
					} else {
						return maxPriceObj;
					}
				},
				{
					value: 0,
					currency: this.product.getPriceCurrency(),
					pricebook: 'default',
				},
			);

			return {
				currency: this.product.getPriceCurrency(),
				value: maxPrice.value,
				pricebook: priceRanges.find((priceRange) => priceRange.maxPrice === maxPrice.value).pricebook,
			};
		}

		return this.product.getPrice();
	}

	/**
	 * Returns the minimal price taking into consideration all the variants
	 * @return {IProductPriceObject} the minimal price
	 */
	getMinPrice(): IProductPriceObject {
		return this.product.getPrice();
	}

	/**
	 * Returns the discount percent of sales price
	 * @return {number} the discount percent
	 */
	getDiscountPercent(): number {
		const minPrice: IProductPriceObject = this.getMinPrice();
		const maxPrice: IProductPriceObject = this.getMaxPrice();

		return ((maxPrice.value - minPrice.value) / maxPrice.value) * 100;
	}
}

export default AbstractPriceModel;
